import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Check, Clock, Info, MapPin, Tag, BoxIcon } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { CalButton } from "../components/ui/CalButton";
import { Card } from "../components/ui/Card";
import { StartHeroImage } from "../components/StartHeroImage";
import { useLanguage } from "../contexts/LanguageContext";
import { startCopy } from "../data/translations/start";
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/636eGguFiQZXSCeJScNMcQ/20260920-Mark-LR35.jpg';
const PORTRAIT_IMAGE = 'https://cdn.magicpatterns.com/uploads/iW4WwChvhBykwEnaEEiJB1/LR21.png';
const FACT_ICONS: BoxIcon[] = [Clock, MapPin, Tag];
export function StartPage() {
  const {
    language
  } = useLanguage();
  const copy = startCopy[language];
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);
  return <div className="min-h-screen bg-paper text-ink-600 font-sans selection:bg-bronze-400 selection:text-white" data-id="element-284" data-mp="11krvhvprq5da">
      <Navigation data-id="element-285" data-mp="wikegrq2zz43" />

      <main data-id="element-286" data-mp="wm88rt3copjv">
        {/* Hero — dark */}
        <section className="hero-curve relative z-10 mt-[108px] md:mt-[120px] pt-20 md:pt-[120px] pb-[100px] md:pb-[150px] bg-navy text-cream-50" data-id="element-287" data-mp="sjjxzfv2t8k9">
          {/* Same treatment as the homepage hero: soft halo, inner cream fade, grain */}
          <div aria-hidden="true" className="hero-curve hero-halo absolute inset-0 -z-10 pointer-events-none" data-id="element-288" data-mp="iro1284q7ohm"></div>
          <div aria-hidden="true" className="hero-curve hero-inner-fade absolute inset-0 z-0 pointer-events-none" data-id="element-289" data-mp="drjgo70p69m4"></div>
          <div className="hero-curve absolute inset-0 bg-grain opacity-20 z-0 mix-blend-overlay pointer-events-none" data-id="element-290" data-mp="4sy3yshwpot9"></div>

          <div className="max-w-6xl mx-auto px-6 sm:px-10 lg:px-12 relative z-10" data-id="element-291" data-mp="3vz90cbbz21e9">
            <div className="grid md:grid-cols-2 gap-10 lg:gap-14 items-center" data-id="element-292" data-mp="3mbdfw6cnnna5">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.8,
              ease: 'easeOut'
            }} data-id="element-293" data-mp="3qdi76yfms7tm">
                <h1 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] leading-tight text-gold-400 mb-4" data-id="element-294" data-mp="3hdbuherrbbvi">
                  {copy.heading}
                </h1>
                <p className="text-cream-50 text-lg md:text-xl leading-relaxed" data-id="element-295" data-mp="dwcws1ve7oyk">
                  {copy.subheading}
                </p>
                <p className="mt-3 text-cream-50/80 text-sm md:text-base leading-relaxed" data-id="element-296" data-mp="3vcba5wxakgfj">
                  {copy.intro}
                </p>
                <ul className="mt-5 flex flex-col lg:flex-row lg:flex-wrap gap-x-6 gap-y-2" data-id="element-297" data-mp="rw4g8i4ohxjy">
                  {copy.facts.map((fact, i) => {
                  const Icon = FACT_ICONS[i] ?? Check;
                  return <li key={fact} className="flex items-center gap-2 text-cream-50 text-sm whitespace-nowrap" data-id="element-298" data-mp="2hcggwr7ybev0" data-mpi={i}>
                        <Icon size={16} className="text-gold-400 flex-shrink-0" aria-hidden="true" data-id="element-299" data-mp="2ceonttvklmp8" />
                        {fact}
                      </li>;
                })}
                </ul>
                <p className="mt-5 text-sm uppercase tracking-widest text-gold-400" data-id="element-300" data-mp="2m3gfe0wpy4zm">
                  {copy.counter}
                </p>
                <div className="mt-5" data-id="element-301" data-mp="1yedxqt234c54">
                  <CalButton tone="dark" data-id="element-302" data-mp="1tfatvbxucbyq">{copy.cta}</CalButton>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              scale: 0.98
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.8,
              delay: 0.2,
              ease: 'easeOut'
            }} data-id="element-303" data-mp="27fbv4si0pleq">
                <StartHeroImage mainImage={HERO_IMAGE} mainAlt={copy.imageAlt} portraitImage={PORTRAIT_IMAGE} portraitAlt={copy.portraitAlt} toggleLabel={copy.portraitToggleLabel} data-id="element-304" data-mp="3dve2x4vvuu43" />
              </motion.div>
            </div>
          </div>
        </section>

        {/* What you get + price, side by side */}
        <section className="surface-a under-start-curve relative pb-20" data-id="element-305" data-mp="mxl2ecwyd890">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10 grid md:grid-cols-2 gap-10 lg:gap-14 items-center" data-id="element-306" data-mp="rwxm4osj1z7d">
            <div data-id="element-307" data-mp="vzaulkynsb7u">
              <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-6" data-id="element-308" data-mp="vxrbznpj5o2w">
                {copy.includesHeading}
              </h2>
              <ul className="space-y-4" data-id="element-309" data-mp="10v9byolkksyr">
                {copy.includes.map(item => <li key={item} className="flex items-start gap-3" data-id="element-310" data-mp="15ouf0v20sd4t">
                    <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-bronze-400/10 flex items-center justify-center" data-id="element-311" data-mp="1anovvrgffrma">
                      <Check size={14} className="text-bronze-600" data-id="element-312" data-mp="1fo35hdxof9mf" />
                    </span>
                    <span className="text-ink-600 leading-relaxed" data-id="element-313" data-mp="1jomzi8mydx4c">{item}</span>
                  </li>)}
              </ul>
            </div>

            <Card className="text-center" data-id="element-314" data-mp="304kdbdxm5r9s">
              <p className="flex flex-wrap items-baseline justify-center gap-x-3 gap-y-1" data-id="element-315" data-mp="303hmf60ajotv">
                <span className="font-serif text-3xl md:text-4xl text-ink-900" data-id="element-316" data-mp="2v6ivsxfu600f">
                  {copy.price}
                </span>
                <span className="text-ink-600 text-base line-through" data-id="element-317" data-mp="2q87k0h5zdffk">
                  {copy.priceNormal}
                </span>
              </p>
              <p className="mt-1 font-serif text-lg text-ink-900" data-id="element-318" data-mp="3iuimjxd5dqf0">
                {copy.priceFor}
              </p>
              <p className="mt-3 text-ink-600 text-base leading-relaxed max-w-md mx-auto mb-6" data-id="element-319" data-mp="3e3abyy02zz09">
                {copy.priceTerms}
              </p>
              <CalButton data-id="element-320" data-mp="393eihsp0fpyy">{copy.cta}</CalButton>
            </Card>
          </div>
        </section>

        {/* What I ask */}
        <section className="surface-b blend-top relative py-20" data-id="element-321" data-mp="5os8zaozz9ni">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-322" data-mp="5mfk0dktnz0y">
            <div data-id="element-323" data-mp="1zxnqqqq62484">
              <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-4" data-id="element-324" data-mp="1zx5rrmc4dbwi">
                {copy.askHeading}
              </h2>
              <p className="text-ink-600 leading-relaxed mb-6" data-id="element-325" data-mp="1q6szf7y2ceft">{copy.askIntro}</p>
              <ul className="space-y-4" data-id="element-326" data-mp="1q4qakr8ncrco">
                {copy.asks.map(item => <li key={item} className="flex items-start gap-3" data-id="element-327" data-mp="1v42xtd3fk0fs">
                    <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-bronze-400/10 flex items-center justify-center" data-id="element-328" data-mp="1g83s293smcdt">
                      <Check size={14} className="text-bronze-600" data-id="element-329" data-mp="1l9192id2h0dq" />
                    </span>
                    <span className="text-ink-600 leading-relaxed" data-id="element-330" data-mp="1799ofys1olqr">{item}</span>
                  </li>)}
              </ul>
              <p className="mt-6 text-ink-600 text-sm leading-relaxed" data-id="element-331" data-mp="1c8w3mdrfoc27">
                {copy.askOptional}
              </p>
            </div>
          </div>
        </section>

        {/* Good to know */}
        <section className="surface-a blend-top relative py-20" data-id="element-332" data-mp="36iq6zs8a9yv6">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10" data-id="element-333" data-mp="3gpj56zvfnsck">
            <div data-id="element-334" data-mp="3bqfxf7o2j80w">
              <h2 className="font-serif text-2xl md:text-3xl text-ink-900 mb-6" data-id="element-335" data-mp="3bqy2mtspkwky">
                {copy.goodToKnowHeading}
              </h2>
              <ul className="space-y-4" data-id="element-336" data-mp="3piw6oq7kc2a9">
                {copy.goodToKnow.map(item => <li key={item} className="flex items-start gap-3" data-id="element-337" data-mp="3korzc5udxs8n">
                    <Info size={18} className="text-bronze-600 mt-1 flex-shrink-0" data-id="element-338" data-mp="391xnzcvi01a" />
                    <span className="text-ink-600 leading-relaxed" data-id="element-339" data-mp="3ugnvhbcwv8a8">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </section>

        {/* Closing */}
        <section className="surface-b blend-top relative pt-24 pb-56" data-id="element-340" data-mp="897k914acwci">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10 text-center" data-id="element-341" data-mp="873gqkaatzcr">
            <h2 className="font-serif text-3xl md:text-4xl text-ink-900 mb-4" data-id="element-342" data-mp="2e265xmlws2o1">
              {copy.closingHeading}
            </h2>
            <p className="text-ink-600 text-lg leading-relaxed mb-8" data-id="element-343" data-mp="2ylhfhr92pyat">
              {copy.closingText}
            </p>
            <CalButton data-id="element-344" data-mp="33jixs9yodtl8">{copy.cta}</CalButton>
          </div>
        </section>
      </main>

      <Footer data-id="element-345" data-mp="38aiube3vu9n7" />
    </div>;
}